import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { BarList, Panel, StackedBarChart, StatTile, fmtNumber, useChartColors } from '../components/charts';
import { DateRangeBar } from '../components/DateRangeBar';
import { dailySeries, rangeForDays } from '../utils/dateRange';
import { fmtTokens, fmtUsd } from '../utils/format';
import { useAppUi } from '../app/useAppUi';

export function DashboardPage() {
  const { t } = useAppUi();
  const colors = useChartColors();
  const [range, setRange] = React.useState(() => rangeForDays(30));
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [ai, setAi] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([
      http.get('/admin/dashboard', { params: range }),
      // Optional: an older backend has no AI usage yet.
      http.get('/admin/ai-usage', { params: range }).catch(() => null),
    ])
      .then(([statsRes, aiRes]) => {
        if (!alive) return;
        setStats(unwrapApiResponse(statsRes));
        setAi(aiRes ? unwrapApiResponse(aiRes) : null);
      })
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [range]);

  const p = stats?.period || {};
  const searchSeries = dailySeries(range, p.searches_by_day, (r) => ({ searches: r?.searches || 0 }));

  return (
    <Box>
      <PageHeader title={t('dashboard.title')} right={<DateRangeBar value={range} onChange={setRange} />} />
      <ErrorBanner error={error} />

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          mb: 1.5,
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        <StatTile
          loading={loading}
          label={t('dashboard.searches')}
          value={fmtNumber(p.searches)}
          hint={t('dashboard.searchersHint', { n: fmtNumber(p.searching_users) })}
        />
        <StatTile
          loading={loading}
          label={t('dashboard.newAccounts')}
          value={fmtNumber((Number(p.new_agents) || 0) + (Number(p.new_bot_users) || 0))}
          hint={t('dashboard.newAccountsHint', { agents: fmtNumber(p.new_agents), bot: fmtNumber(p.new_bot_users) })}
        />
        <StatTile
          loading={loading}
          label={t('dashboard.paidAccounts')}
          value={fmtNumber(p.paid_accounts)}
          hint={t('dashboard.paidHint', { trial: fmtNumber(p.trial_accounts), revenue: fmtUsd(p.revenue_usd) })}
        />
        <StatTile
          loading={loading}
          label={t('dashboard.aiCost')}
          value={ai ? fmtUsd(ai.totals?.cost_usd) : '—'}
          hint={ai ? t('dashboard.aiHint', { tokens: fmtTokens(ai.totals?.total_tokens) }) : ''}
        />
      </Box>

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' } }}>
        <Panel title={t('dashboard.searchesPerDay')}>
          <StackedBarChart
            data={searchSeries}
            series={[{ key: 'searches', label: t('dashboard.searches'), color: colors.series[0] }]}
            loading={loading}
            emptyText={t('dashboard.noSearches')}
          />
        </Panel>

        <Stack spacing={1.5}>
          <Panel title={t('dashboard.topCountries')} sx={{ height: 'auto' }}>
            <BarList
              loading={loading}
              color={colors.series[0]}
              emptyText={t('dashboard.noSearches')}
              items={(p.top_countries || []).map((c) => ({ key: c.country, label: c.country, value: c.searches }))}
            />
          </Panel>
          <Panel
            sx={{ height: 'auto', flex: 1 }}
            title={t('dashboard.aiUsage')}
            action={
              <Button
                component={RouterLink}
                to="/ai-usage"
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                sx={{ color: 'text.secondary', mr: -1 }}
              >
                {t('dashboard.open')}
              </Button>
            }
          >
            <BarList
              loading={loading}
              color={colors.series[1]}
              emptyText={t('ai.noUsage')}
              formatValue={fmtTokens}
              items={(ai?.byModel || []).map((m) => ({
                key: m.model,
                label: m.model,
                value: m.total_tokens,
                sub: Number(m.cost_usd) > 0 ? fmtUsd(m.cost_usd) : t('ai.free'),
              }))}
            />
          </Panel>
        </Stack>
      </Box>

      <Typography sx={{ mt: 2, fontSize: 12, color: 'text.disabled' }}>{t('dashboard.footnote')}</Typography>
    </Box>
  );
}
